/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {sendGaPageView, trackPlatformAction} from '../../util/analytics';
import {NOTIFICATIONS_ROUTE, SUBSCRIPTION_ROUTE} from '../../util/constants';
import {onSetCrumbs} from '../../util/events';
import {AppContext} from '../misc/appContext';
import {SubscriptionModuleName} from '../../util/enums';
import {createErrorMessage} from '../../util/utils';
import {ModelResponse, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import Notification, {defaultNotificationValues, FREE_NOTIFICATION_TYPES} from '../../models/notificationModel';
import {DeviceTokenChannel, sendTestNotification, updateNotificationChannels} from '../../util/requestSender';
import NotificationsPage from './notificationsPage';
import {AccessKey, ActivateHandle} from '../../util/interfaces';
import useRight from '../misc/useRight';
import AddNotification, {AddNotificationSubmit} from './addNotification/addNotification';
import PushChannelsProvider from '../pushNotifications/pushChannelsContext';
import {onShowConfirmSnackbar} from '../../util/renderUtil';
import {ensurePushChannel, readNotificationChannels, readNotificationPushDeviceIds} from '../../util/pushChannelUtil';
import {isPushPromptInFlight} from '../../hooks/usePushSubscription';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router';

export default function NotificationsPageController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const {t, i18n} = useTranslation();
    const [canEdit, canDelete] = useRight(AccessKey.NOTIFICATIONS_EDIT, AccessKey.NOTIFICATIONS_DELETE);
    const [notifications, setNotifications] = useState<Array<Notification>>([]);
    const [loading, setLoading] = useState(true);
    // Blocks a double test-send while one is in flight (the paper-plane fires
    // the send immediately — there is no dialog anymore).
    const sendingTestRef = useRef(false);
    const addRef = useRef<ActivateHandle>(null);
    const location = useLocation();
    const navigate = useNavigate();
    // PWA-28: fire the first-run "open add-wizard when empty" intent only once.
    const firstRunPushIntent = (location.state as {firstRunPushEnable?: boolean} | null)?.firstRunPushEnable === true;
    const firstRunHandledRef = useRef(false);
    const moduleActive = useMemo(() => {
        return !!user?.subscription?.hasActiveModule(SubscriptionModuleName.NOTIFICATIONS);
    }, [user]);

    function onActivateModule() {
        navigate(SUBSCRIPTION_ROUTE);
    }

    function onClickAdd() {
        if (!canEdit) return;
        // if (!moduleActive) return onPulseModule();
        addRef.current?.activate();
    }

    // Resolves true once the notification actually landed (create succeeded) —
    // the wizard gates its success step on this. A failed create, or an
    // early-return (no rights / a premium type without an active module),
    // resolves false so the wizard stays on the channels step.
    function onAddNotification(values: AddNotificationSubmit): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            if (!account || !canEdit) return resolve(false);
            if (!moduleActive && !FREE_NOTIFICATION_TYPES.includes(values.notificationType)) {
                onPulseModule();
                return resolve(false);
            }
            setLoading(true);
            // The channels the add-wizard collected. Create doesn't carry channels
            // (backend defaults to EMAIL), so anything else is PATCHed right after.
            const channels: Array<DeviceTokenChannel> = [
                ...(values.emailEnabled ? (['EMAIL'] as Array<DeviceTokenChannel>) : []),
                ...(values.pushEnabled ? (['WEB_PUSH'] as Array<DeviceTokenChannel>) : [])
            ];
            const notification = new Notification({
                notification_type: values.notificationType,
                // Only ship the typed address when the e-mail channel is ON. A
                // push-only notification must fall back to the account e-mail:
                // a half-typed/invalid residual address (the wizard only
                // validates it when e-mail is selected) would make the backend's
                // EmailStr reject the whole create.
                email_addresses: [values.emailEnabled ? values.emailAddress || account.email : account.email],
                account_id: account.id,
                ...defaultNotificationValues[values.notificationType]
            });
            notification.create((notification: Notification, success: boolean, data: ResponseData) => {
                if (!success) {
                    setLoading(false);
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'notifications.list.notificationAddErrorMessage',
                                    'Het toevoegen van de notificatie is niet gelukt.'
                                )
                        ),
                        t('notifications.list.notificationAddErrorTitle', 'Toevoegen mislukt')
                    );
                    return resolve(false);
                }
                // The notification exists now. `channelsSaved` says whether the
                // follow-up channel/targeting PATCH landed.
                const finish = (channelsSaved: boolean) => {
                    setLoading(false);
                    // Functional update: a focus-resync GET or a second quick
                    // create may have replaced the list since this closure was
                    // captured — concat onto the LATEST list, not a stale snapshot.
                    setNotifications(previous => previous.concat(notification));
                    if (channelsSaved)
                        onShowConfirmSnackbar(
                            t('notifications.list.notificationAddedSnackbar', 'De notificatie is toegevoegd')
                        );
                    // A failed channel PATCH leaves the notification on the
                    // default e-mail channel — warn instead of a green "added",
                    // so the promise of push on the wizard's success step isn't
                    // silently broken.
                    else
                        showSnackbar(
                            t(
                                'notifications.list.notificationChannelsNotSavedSnackbar',
                                'De notificatie is toegevoegd, maar de bezorgkanalen konden niet worden opgeslagen. Pas ze aan op de kaart.'
                            ),
                            {backgroundColor: 'color-blue-bolmate', textColor: 'white'}
                        );
                    resolve(true);
                };
                const isDefaultChannels = values.emailEnabled && !values.pushEnabled;
                if (isDefaultChannels) return finish(true);
                updateNotificationChannels({
                    notification_id: notification.id,
                    account_id: account.id,
                    channels,
                    // Per-notification device targeting from the wizard: ALWAYS
                    // the explicit token-id list when push is on. Omitting the
                    // field would leave the fresh notification's targeting
                    // absent (= all devices, incl. future subscriptions) — the
                    // all-configs-flip bug.
                    ...(values.pushEnabled ? {push_device_ids: values.pushDeviceIds} : {}),
                    callback: (channelData: ResponseData) => {
                        if (channelData.success) {
                            // Adopt the SERVER-normalized channels + targeting
                            // from the response (mirrors patchChannels in
                            // notificationChannelsSelect) so the fresh card reads
                            // its real device targeting. Without this the card
                            // would read null (= all devices) and the first
                            // uncheck would PATCH the full list minus one,
                            // silently restoring the devices deselected here.
                            const serverNotification = (
                                channelData as {notification?: {channels?: Array<string> | null; flex?: unknown}}
                            ).notification;
                            notification.channels = serverNotification
                                ? readNotificationChannels(serverNotification)
                                : channels;
                            const serverDeviceIds = serverNotification
                                ? readNotificationPushDeviceIds(serverNotification)
                                : values.pushDeviceIds.length
                                  ? values.pushDeviceIds
                                  : null;
                            const flex = {...(notification.flex ?? {})};
                            if (serverDeviceIds === null) delete flex.push_device_ids;
                            else flex.push_device_ids = serverDeviceIds;
                            notification.flex = flex;
                        }
                        finish(!!channelData.success);
                    },
                    errorCallback: () => finish(false)
                });
            });
        });
    }

    function onChangeNotification(notification: Notification, onSaved?: ModelResponse<Notification>) {
        if (!canEdit) return;
        const _notifications = notifications.slice();
        for (let ix = 0; ix < _notifications.length; ix++) {
            if (_notifications[ix].id !== notification.id) continue;
            // onSaved (when passed, e.g. the e-mail dialog) fires on the
            // persist result so the caller can confirm success.
            notification.update(onSaved);
            trackPlatformAction('notifications', 'save_settings');
            _notifications.splice(ix, 1, notification);
            break;
        }
        setNotifications(_notifications);
    }

    function onReplaceNotification(notification: Notification) {
        const _notifications = notifications.slice();
        for (let ix = 0; ix < notifications.length; ix++) {
            if (_notifications[ix].id === notification.id) {
                _notifications.splice(ix, 1, notification);
                break;
            }
        }
        setNotifications(_notifications);
    }

    /** Drop the first-run intent from the history entry once it has been acted
     *  on. Without this a later reload of /notificaties replays it — including
     *  on notifications the user has since deliberately taken off push. */
    function clearFirstRunPushIntent() {
        if (!firstRunPushIntent) return;
        navigate(NOTIFICATIONS_ROUTE, {replace: true, state: null});
    }

    function onEnablePushOnAll(deviceId: string) {
        if (!account || !canEdit) return;
        // Notifications this device is not yet a push target for.
        const targets = notifications.filter(notification => {
            const currentChannels = readNotificationChannels(notification);
            const rawIds = readNotificationPushDeviceIds(notification);
            return !(currentChannels.includes('WEB_PUSH') && (rawIds === null || rawIds.includes(deviceId)));
        });
        clearFirstRunPushIntent();
        if (!targets.length) return;
        let pending = targets.length;
        // Re-fetch once every PATCH has settled. Mutating the existing model
        // instances is not enough: each card's channels select resyncs from its
        // `notification` prop by IDENTITY, so an in-place update leaves every
        // select rendering its pre-PATCH state until the page is reloaded.
        // Fresh instances from the backend also make this the server's truth.
        const finish = () => {
            if (--pending > 0) return;
            loadNotifications();
        };
        targets.forEach(notification => {
            const currentChannels = readNotificationChannels(notification);
            const channels = ensurePushChannel(currentChannels);
            const rawIds = readNotificationPushDeviceIds(notification);
            // Turn on THIS device and nothing else. Devices already receiving
            // this notification stay (silently dropping another phone's push is
            // not ours to do), but a stored targeting list is only carried over
            // while the push channel is actually ON — a notification with push
            // OFF may still hold ids from an earlier configuration, and merging
            // those would light up every one of those devices instead of the
            // single device the user just said yes on.
            const keptIds = currentChannels.includes('WEB_PUSH') && rawIds !== null ? rawIds : [];
            const pushDeviceIds = keptIds.includes(deviceId) ? keptIds : [...keptIds, deviceId];
            updateNotificationChannels({
                notification_id: notification.id,
                account_id: account.id,
                channels,
                push_device_ids: pushDeviceIds,
                callback: (_channelData: ResponseData) => finish(),
                // Non-2xx / network failure never fires `callback` — without
                // this the counter would never reach zero and the list would
                // never refresh.
                errorCallback: finish
            });
        });
    }

    // Fires the test send IMMEDIATELY — no confirmation dialog. The test goes
    // out over the notification's own configuration (its e-mail address(es)
    // and, when the WEB_PUSH channel is on, its push devices).
    function onSendTestNotification(notification: Notification) {
        return function () {
            if (sendingTestRef.current) return;
            sendingTestRef.current = true;
            sendTestNotification({
                data: {
                    account_id: notification.accountId,
                    // Same default the old dialog used when the notification
                    // carried no address: the logged-in user's e-mail.
                    email_address: notification.firstEmailAddress ?? user?.email ?? '',
                    notification_id: notification.id
                },
                callback: (data: ResponseData) => {
                    sendingTestRef.current = false;
                    if (data.success) {
                        onShowConfirmSnackbar(
                            t('notifications.list.notificationSentSnackbar', 'De notificatie is verzonden')
                        );
                    } else {
                        // Translate ONLY known backend error CODES (EMAIL_SEND_FAILED,
                        // NOTIFICATION_NOT_FOUND, ...). Building
                        // notifications.errors.<arbitrary message> for a non-code (a
                        // full sentence the backend already localized) logs a spurious
                        // "missing translation" warning — so fall back to the raw
                        // message, or a generic error if none was returned.
                        const code = data.message;
                        const message =
                            code && i18n.exists(`notifications.errors.${code}`)
                                ? t(`notifications.errors.${code}`)
                                : code ||
                                  t(
                                      'notifications.list.notificationSendErrorMessage',
                                      'Het verzenden van de notificatie is niet gelukt.'
                                  );
                        showDialog(
                            createErrorMessage(message),
                            t('notifications.list.notificationSendErrorTitle', 'Verzenden mislukt')
                        );
                    }
                },
                // `callback` only fires on 2xx — release the guard on network
                // failure too, or the button would go dead until a reload.
                errorCallback: () => {
                    sendingTestRef.current = false;
                    showDialog(
                        createErrorMessage(
                            t(
                                'notifications.list.notificationSendErrorMessage',
                                'Het verzenden van de notificatie is niet gelukt.'
                            )
                        ),
                        t('notifications.list.notificationSendErrorTitle', 'Verzenden mislukt')
                    );
                }
            });
        };
    }

    function onDeleteNotification(notification: Notification) {
        return function () {
            if (!canDelete) return;
            showDialog(
                t('notifications.list.deleteConfirmMessage', 'Weet je zeker dat je deze notificatie wilt verwijderen?'),
                t('notifications.list.deleteConfirmTitle', 'Notificatie verwijderen'),
                [
                    t('notifications.list.deleteConfirmNo', 'Nee'),
                    {
                        label: t('notifications.list.deleteConfirmYes', 'Ja'),
                        onClick: onConfirmDelete(notification),
                        primary: false
                    }
                ]
            );
        };
    }

    function onConfirmDelete(notification: Notification) {
        return function () {
            setLoading(true);
            notification.delete((_: Notification, success: boolean, data: ResponseData) => {
                setLoading(false);
                // A not-found means the notification is already gone server-side
                // (deleted in another tab, or a double submit). Delete is
                // idempotent: drop the row and confirm instead of surfacing a
                // stale-state error.
                const alreadyDeleted =
                    !success && (data as {error_code?: string}).error_code === 'notification_not_found';
                if (success || alreadyDeleted) {
                    setNotifications((notifications: Array<Notification>) =>
                        notifications.filter((noti: Notification) => noti.id !== notification.id)
                    );
                    onShowConfirmSnackbar(
                        t('notifications.list.notificationDeletedSnackbar', 'De notificatie is verwijderd')
                    );
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'notifications.list.notificationDeleteErrorMessage',
                                    'Het verwijderen van de notificatie is niet gelukt.'
                                )
                        ),
                        t('notifications.list.notificationDeleteErrorTitle', 'Verwijderen mislukt')
                    );
            });
        };
    }

    function onPulseModule() {
        const button = document.getElementById('btn-activate');
        if (!button) return;
        button.addEventListener('animationend', () => {
            button.classList.remove('pulse');
        });
        button.classList.add('pulse');
    }

    function loadNotifications() {
        setLoading(true);
        if (!account) return setLoading(false);
        Notification.getAll(account.id, (notifications: Array<Notification>, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                setNotifications(notifications);
                if (firstRunPushIntent && !firstRunHandledRef.current && notifications.length === 0 && canEdit) {
                    firstRunHandledRef.current = true;
                    addRef.current?.activate();
                }
            } else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('notifications.list.loadErrorMessage', 'Er zijn geen notificaties gevonden.')
                    ),
                    t('notifications.list.loadErrorTitle', 'Notificaties niet gevonden')
                );
        });
    }

    useEffect(() => {
        sendGaPageView(NOTIFICATIONS_ROUTE);
        onSetCrumbs([{label: t('notifications.list.breadcrumb', 'Notificaties')}]);
    }, []);

    useEffect(() => {
        loadNotifications();
    }, [account?.id]);

    // Re-fetch notifications when the user returns to the tab, so a channel
    // change made in ANOTHER browser/tab (which devices / e-mails are active for
    // a notification) shows up here instead of a stale snapshot. The backend is
    // the source of truth; this is the sync that keeps every session in step.
    useEffect(() => {
        // visibilitychange ONLY — deliberately NO 'focus' listener. Returning
        // to the tab fires focus AND visibilitychange back-to-back, which made
        // loadNotifications() run twice per tab switch. Skip while a push
        // permission prompt is in flight: the OS dialog toggles visibility and
        // a resync then competes with it for the main thread. Mirrors the same
        // guard on notificationsPage.tsx / pushChannelsContext.tsx.
        const resync = () => {
            if (document.visibilityState === 'visible' && !isPushPromptInFlight()) loadNotifications();
        };
        document.addEventListener('visibilitychange', resync);
        return () => document.removeEventListener('visibilitychange', resync);
    }, [account?.id]);

    // One PushChannelsProvider for the page AND the add-wizard: both read the
    // same devices/subscription state, and the wizard can run the subscribe
    // flow for this device.
    return (
        <PushChannelsProvider>
            <NotificationsPage
                onSendTestNotification={onSendTestNotification}
                onClickAdd={onClickAdd}
                onChangeNotification={onChangeNotification}
                onDeleteNotification={onDeleteNotification}
                onReplaceNotification={onReplaceNotification}
                loading={loading}
                notifications={notifications}
                moduleActive={moduleActive}
                canEdit={canEdit}
                canDelete={canDelete}
                onReloadNotifications={loadNotifications}
                firstRunPushEnable={firstRunPushIntent}
                onEnablePushOnAll={onEnablePushOnAll}
            />
            <AddNotification
                onActivateModule={onActivateModule}
                onSubmit={onAddNotification}
                ref={addRef}
                moduleActive={moduleActive}
                notifications={notifications}
            />
        </PushChannelsProvider>
    );
}
